import React, { useEffect, useRef, useState } from "react";
import { Film, UploadCloud, Loader2, Trash2, Pencil, Check, X, Play, Eye, ImagePlus } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { adminApi, uploadImage, imgUrl, mediaUrl, getToken, captureVideoThumb, fmtDuration, errMsg } from "../lib/api";
import { LibraryPicker } from "./MembersPanel";

const MAX = 200 * 1024 * 1024;

function Uploader({ onDone }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [thumb, setThumb] = useState(null); // {file?, url?, local}
  const [duration, setDuration] = useState(null);
  const [f, setF] = useState({ title: "", description: "" });
  const [progress, setProgress] = useState(null);

  const pick = async (fl) => {
    if (!fl) return;
    if (!fl.type.startsWith("video/")) return toast.error("Please choose a video file (mp4, webm, mov)");
    if (fl.size > MAX) return toast.error("Video must be under 200MB");
    setFile(fl);
    setPreview(URL.createObjectURL(fl));
    setF((x) => ({ ...x, title: x.title || fl.name.replace(/\.[^.]+$/, "") }));
    const { thumb: t, duration: d } = await captureVideoThumb(fl);
    setDuration(d);
    if (t) setThumb({ file: t, local: URL.createObjectURL(t) });
  };

  const reset = () => { setFile(null); setPreview(null); setThumb(null); setDuration(null); setF({ title: "", description: "" }); setProgress(null); };

  const submit = async () => {
    if (!file || !f.title.trim()) return toast.error("Choose a video and add a title");
    try {
      setProgress(0);
      let thumbnail_url = thumb?.url || null;
      if (thumb?.file) thumbnail_url = await uploadImage(thumb.file, () => {});
      const url = await uploadImage(file, (p) => setProgress(Math.min(99, p)));
      await adminApi.post("/admin/videos", { title: f.title.trim(), description: f.description, url, thumbnail_url, duration });
      toast.success("Video uploaded – it'll be live for VIPs once processing finishes");
      reset();
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
      setProgress(null);
    }
  };

  if (!file) {
    return (
      <div className="dropzone p-8 text-center cursor-pointer" onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }} data-testid="video-dropzone">
        <Film className="w-10 h-10 mx-auto text-[#ee3a70]" />
        <p className="mt-3 font-medium">Drop a video here or click to choose</p>
        <p className="text-[13px] text-[#7a7276] mt-1">MP4, WEBM or MOV · up to 200MB · shown to active VIPs in their "For You" section</p>
        <input ref={ref} type="file" accept="video/mp4,video/webm,video/quicktime,.mov,.m4v" className="hidden" data-testid="video-input"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    );
  }

  return (
    <div className="admin-card p-5 grid md:grid-cols-[320px_1fr] gap-5" data-testid="video-upload-form">
      <div>
        <video src={preview} controls muted className="w-full rounded-xl bg-black aspect-video" />
        <div className="flex items-center gap-3 mt-3">
          <div className="w-24 aspect-video rounded-md overflow-hidden bg-[#f6eef1] grid place-items-center">
            {thumb ? <img src={thumb.local || imgUrl(thumb.url)} alt="" className="w-full h-full object-cover" /> : <Film className="w-5 h-5 text-[#a79fa3]" />}
          </div>
          <div className="text-[12.5px] text-[#7a7276]">
            <p>Thumbnail {thumb?.file ? "(auto from video)" : thumb?.url ? "(from library)" : "(none)"}</p>
            <LibraryPicker onPick={(u) => setThumb({ url: u })} trigger={<button type="button" className="text-[#ee3a70] hover:underline flex items-center gap-1 mt-0.5" data-testid="video-thumb-pick"><ImagePlus className="w-3.5 h-3.5" /> Use library photo</button>} />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" maxLength={120} data-testid="video-title" />
        <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} placeholder="A little note for your VIPs (optional)" data-testid="video-description" />
        <p className="text-[12.5px] text-[#7a7276]">{file.name} · {(file.size / 1048576).toFixed(1)}MB {duration ? `· ${fmtDuration(duration)}` : ""}</p>
        {progress !== null && (
          <div data-testid="video-upload-progress"><Progress value={progress} className="h-2" /><p className="text-[12px] text-[#7a7276] mt-1">Uploading {progress}% – keep this tab open</p></div>
        )}
        <div className="flex gap-2">
          <button onClick={submit} disabled={progress !== null} className="admin-btn h-10" data-testid="video-upload-btn">
            {progress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload for VIPs
          </button>
          <button onClick={reset} disabled={progress !== null} className="admin-ghost h-10">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function VideoRow({ v, onPlay, onDelete, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ title: v.title, description: v.description || "" });
  const save = async () => {
    try { await adminApi.patch(`/admin/videos/${v.id}`, f); toast.success("Saved"); setEdit(false); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ready = v.status === "ready";
  return (
    <div className="admin-card overflow-hidden" data-testid={`admin-video-${v.id}`}>
      <button onClick={() => ready && onPlay(v)} className="relative block w-full aspect-video bg-[#1b1719] group" disabled={!ready}>
        {v.thumbnail_url && <img src={imgUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover opacity-90" />}
        <span className="absolute inset-0 grid place-items-center">
          {ready ? <span className="w-12 h-12 rounded-full bg-white/90 grid place-items-center group-hover:scale-110 transition-transform"><Play className="w-5 h-5 text-[#ee3a70] ml-0.5" fill="currentColor" /></span>
            : <span className="text-white text-[13px] flex items-center gap-2 bg-black/50 px-3 py-1.5 rounded-full" data-testid="video-status">{v.status === "processing" ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing</> : "Upload failed"}</span>}
        </span>
        {v.duration && <span className="absolute bottom-2 right-2 text-[11px] text-white bg-black/60 rounded px-1.5 py-0.5">{fmtDuration(v.duration)}</span>}
      </button>
      <div className="p-3">
        {edit ? (
          <div className="space-y-2">
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="h-9" />
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} />
          </div>
        ) : (
          <>
            <p className="font-semibold text-[14.5px] truncate">{v.title}</p>
            <p className="text-[12.5px] text-[#7a7276] line-clamp-1">{v.description || "–"}</p>
          </>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-[12px] text-[#7a7276] flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {v.views || 0} views · {v.size ? `${(v.size / 1048576).toFixed(1)}MB` : ""}</span>
          <div className="flex gap-1.5">
            {edit ? <><button className="icon-btn" onClick={save} data-testid="video-save"><Check className="w-4 h-4" /></button><button className="icon-btn" onClick={() => setEdit(false)}><X className="w-4 h-4" /></button></>
              : <><button className="icon-btn" onClick={() => setEdit(true)} data-testid="video-edit"><Pencil className="w-4 h-4" /></button><button className="icon-btn danger" onClick={() => onDelete(v)} data-testid="video-delete"><Trash2 className="w-4 h-4" /></button></>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VideosPanel() {
  const [videos, setVideos] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [del, setDel] = useState(null);
  const load = async () => { try { const { data } = await adminApi.get("/admin/videos"); setVideos(data); } catch (e) { toast.error(errMsg(e)); setVideos([]); } };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!videos?.some((v) => v.status === "processing")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [videos]);

  const confirmDel = async () => {
    const v = del; setDel(null);
    try { await adminApi.delete(`/admin/videos/${v.id}`); toast.success("Video deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Uploader onDone={load} />
      <h2 className="text-[18px] font-semibold mt-8 mb-4">VIP videos <span className="text-[#a79fa3] font-normal">({videos?.length || 0})</span></h2>
      {!videos ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#ee3a70]" /> : !videos.length ? (
        <p className="text-[14px] text-[#7a7276] text-center py-10" data-testid="videos-empty">No videos yet. Upload your first one above.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-videos-grid">
          {videos.map((v) => <VideoRow key={v.id} v={v} onPlay={setPlaying} onDelete={setDel} onSaved={load} />)}
        </div>
      )}

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-[860px] p-0 overflow-hidden bg-black border-0">
          <DialogTitle className="sr-only">{playing?.title}</DialogTitle>
          {playing && <video src={mediaUrl(playing.url, getToken())} controls autoPlay playsInline className="w-full max-h-[80vh] bg-black" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this video?</AlertDialogTitle><AlertDialogDescription>VIP members will no longer be able to watch it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDel} className="bg-[#dc2626] hover:bg-[#b91c1c]" data-testid="video-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
